// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Section} from 'common/components/items-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {ContextPanelData} from '../context-panel/types';
import {template} from './template';
import css from './index.inline.styl';
import {ControlBarAttrs, ControlBarJsxProps, ControlBarParams, ControlBarState} from './types';

export class ControlBar extends Component<
  ControlBarState,
  ControlBarAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  ControlBarParams
> {
  private _sectionsFn: () => Section[] = null;

  static get attrsSchema(): AttrsSchema<ControlBarAttrs> {
    return {
      ...super.attrsSchema,
      controls: {
        type: `json`,
        description: `interface: Section`,
      },
      theme: {
        type: `string`,
        description: `Theme applied to untruncated buttons`,
        default: `tertiary`,
        enum: Object.values(ButtonTheme),
      },
      size: {
        type: `string`,
        description: `Size applied to buttons`,
        default: `medium`,
        enum: Object.values(ButtonSize),
      },
      'overflow-icon': {
        type: `string`,
        description: `Custom overflow icon for the mp-select dropdown button`,
        default: `hor-ellipsis`,
      },
      'overflow-tooltip': `json`,
      'overflow-menu-width': {
        type: `number`,
        default: 160,
      },
      'overflow-menu-height': {
        type: `number`,
        default: 300,
      },
      'overflow-menu-align-right': {
        type: `boolean`,
        description: `aligns the overflow menu's right border with the overflow button`,
      },
      disabled: `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      params: {
        overflowControls: Array,
        controls: Object,
      },
      defaultParams: {
        overflowControls: [],
      },
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        menuOpen: false,
        contextPanelTransform: null,
      },
      helpers: {
        openMenu: () => this.update({menuOpen: true}),
        onDropMenuStateChange: (ev) => this.update({menuOpen: ev.detail.state === `open`}),
        getOverflowItems: () => {
          const items = this._sectionsFn?.() ?? this.params.overflowControls;
          const totalItems = items.reduce((a, section) => a + section.items.length, 0);

          if (totalItems < 1) {
            return [];
          }

          return items;
        },

        handleMenuClick: (ev) => {
          const {value, name, additionalInfo} = ev.detail.item;

          this.dispatchEvent(new CustomEvent(`select`, {detail: {value, name, additionalInfo}}));
          this.update({menuOpen: false});
        },

        handleClick: (ev) => {
          const value = ev.target.getAttribute(`value`);
          this.selectOption(value);
        },
      },
    };
  }

  public selectOption(value) {
    this.dispatchEvent(new CustomEvent(`select`, {detail: {value}}));
  }

  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(value: (item: any) => ContextPanelData) {
    this.update({contextPanelTransform: value});
  }

  set sectionsFn(value) {
    this._sectionsFn = value;
    this.update();
  }
}

export const ControlBarEl = defineAndMakeJsxFactory<ControlBarJsxProps>(`mp-control-bar`, ControlBar);
export const ControlBarReact = panel2react<ControlBarJsxProps>(`mp-control-bar`, ControlBar);
